import React, { useMemo } from 'react';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { Box, Button, InputAdornment, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid, type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid';
import { clampQuantity, hasSelection } from '@/components/bulk-dispose/utils';
import type { BulkDisposeConditionCode, StockDisposeRow } from '@/components/bulk-dispose/types';
import { useWriteGuard } from '@/hooks/useWriteGuard';

const CONDITION_CODE_OPTIONS: BulkDisposeConditionCode[] = ['H', 'A', 'B'];

type StockDisposeTabProps = {
  rows: StockDisposeRow[];
  isFetching: boolean;
  searchFilter: string;
  selectedRowIds: GridRowSelectionModel;
  qtyByRow: Record<string, number>;
  selectedQty: number;
  totalQty: number;
  isCompleting: boolean;
  onSearchFilterChange: (value: string) => void;
  onClearFilters: () => void;
  onQtyChange: (row: StockDisposeRow, rawValue: string) => void;
  onConditionCodeChange: (row: StockDisposeRow, conditionCode: BulkDisposeConditionCode) => void;
  onSelectionChange: (selection: GridRowSelectionModel) => void;
  onOpenConfirm: () => void;
};

export const StockDisposeTab: React.FC<StockDisposeTabProps> = ({
  rows,
  isFetching,
  searchFilter,
  selectedRowIds,
  qtyByRow,
  selectedQty,
  totalQty,
  isCompleting,
  onSearchFilterChange,
  onClearFilters,
  onQtyChange,
  onConditionCodeChange,
  onSelectionChange,
  onOpenConfirm,
}) => {
  const isViewer = useWriteGuard();
  const columns = useMemo<GridColDef<StockDisposeRow>[]>(
    () => [
      {
        field: 'lin',
        headerName: 'LIN',
        width: 140,
      },
      {
        field: 'nsn',
        headerName: 'NSN',
        width: 190,
      },
      {
        field: 'assetName',
        headerName: 'Nomenclature',
        flex: 1,
        minWidth: 220,
      },
      {
        field: 'location',
        headerName: 'Location',
        flex: 1,
        minWidth: 240,
      },
      {
        field: 'conditionCode',
        headerName: 'Condition Code',
        width: 180,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const value = row.conditionCode ?? '';

          return (
            <TextField
              select
              size="small"
              value={value}
              disabled={isViewer}
              onChange={(event) => {
                const nextCode = event.target.value as BulkDisposeConditionCode;
                if (!CONDITION_CODE_OPTIONS.includes(nextCode)) {
                  return;
                }
                onConditionCodeChange(row, nextCode);
              }}
              sx={{ width: 150 }}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              {CONDITION_CODE_OPTIONS.map((conditionCode) => (
                <MenuItem key={conditionCode} value={conditionCode}>
                  {conditionCode}
                </MenuItem>
              ))}
            </TextField>
          );
        },
      },
      {
        field: 'qty',
        headerName: 'Qty',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const selected = hasSelection(selectedRowIds, row.id);
          const fallback = selected ? row.availableCount : 0;
          const value = clampQuantity(qtyByRow[row.id] ?? fallback, row.availableCount);

          return (
            <TextField
              size="small"
              type="number"
              value={value}
              inputProps={{ min: 0, max: row.availableCount, style: { textAlign: 'right' } }}
              disabled={isViewer}
              onChange={(event) => onQtyChange(row, event.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">/ {row.availableCount}</InputAdornment>
                ),
              }}
              sx={{ width: 120 }}
            />
          );
        },
      },
    ],
    [onConditionCodeChange, onQtyChange, qtyByRow, selectedRowIds, isViewer],
  );

  return (
    <>
      <Paper elevation={1} sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 10 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search assets, LIN, NSN, location..."
              value={searchFilter}
              onChange={(event) => onSearchFilterChange(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Button fullWidth variant="text" onClick={onClearFilters}>
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={1} sx={{ p: 2 }}>
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={isFetching}
            autoHeight
            checkboxSelection={!isViewer}
            disableRowSelectionOnClick
            rowSelectionModel={selectedRowIds}
            onRowSelectionModelChange={onSelectionChange}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'background.default',
              },
            }}
          />
        </Box>

        <Stack spacing={1} alignItems="flex-end" sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" textAlign="right">
            {selectedQty} of {totalQty} items selected
          </Typography>
          <Button
            variant="contained"
            color="warning"
            onClick={onOpenConfirm}
            disabled={isCompleting || isViewer || selectedQty <= 0}
          >
            Generate 1348
          </Button>
        </Stack>
      </Paper>
    </>
  );
};
